import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Upload, X, ArrowRight } from './icons';
import { useNavigate } from 'react-router-dom';
import { rgbToHex } from '../utils/colors';

interface ExtractedColor {
  hex: string;
  r: number;
  g: number;
  b: number;
  count: number;
}

const BUCKET = 32; // quantization step — 256/32 = 8 buckets per channel

interface ExtractionResult {
  colors: ExtractedColor[];
  totalPixels: number;
  filteredPixels: number;
}

const extractColors = (imageData: ImageData, topN = 6): ExtractionResult => {
  const histogram = new Map<number, { r: number; g: number; b: number; count: number }>();
  const { data } = imageData;
  const totalPixels = data.length / 4;
  let filteredPixels = 0;

  for (let i = 0; i < data.length; i += 4) {
    const a = data[i + 3];
    if (a < 128) continue; // skip transparent pixels

    const r = Math.round(data[i] / BUCKET) * BUCKET;
    const g = Math.round(data[i + 1] / BUCKET) * BUCKET;
    const b = Math.round(data[i + 2] / BUCKET) * BUCKET;

    // Skip near-white and near-black to surface interesting palette colors
    const brightness = (r + g + b) / 3;
    if (brightness > 240 || brightness < 15) {
      filteredPixels++;
      continue;
    }

    const key = (r << 16) | (g << 8) | b;
    const existing = histogram.get(key);
    if (existing) { existing.count++; } else { histogram.set(key, { r, g, b, count: 1 }); }
  }

  const colors = Array.from(histogram.values())
    .sort((a, b) => b.count - a.count)
    .slice(0, topN)
    .map(({ r, g, b, count }) => ({ hex: rgbToHex(r, g, b), r, g, b, count }));

  return { colors, totalPixels, filteredPixels };
};

const drawImageToCanvas = (
  file: File,
  canvas: HTMLCanvasElement,
): Promise<ImageData> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      const MAX = 200;
      const scale = Math.min(MAX / img.width, MAX / img.height, 1);
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        URL.revokeObjectURL(url);
        return reject(new Error('Canvas context unavailable'));
      }
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(imageData);
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };

    img.src = url;
  });

const ImageColorExtractor: React.FC = () => {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [colors, setColors] = useState<ExtractedColor[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Revoke preview URL on cleanup or reset
  const revokePreview = useCallback(() => {
    setPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Revoke any lingering preview URL
      setPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, []);

  const processFile = useCallback(async (file: File) => {
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    if (file.size > MAX_FILE_SIZE) {
      setError('File is too large. Please use an image under 10MB.');
      return;
    }
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file (PNG, JPG, WebP, etc.)');
      return;
    }
    setError(null);
    setLoading(true);
    setSelected([]);

    // Revoke previous preview URL before creating a new one
    revokePreview();
    const previewUrl = URL.createObjectURL(file);
    setPreview(previewUrl);

    try {
      const canvas = canvasRef.current;
      if (!canvas) {
        setError('Unable to process image. Please try again.');
        setLoading(false);
        return;
      }

      const imageData = await drawImageToCanvas(file, canvas);
      const { colors: extracted, totalPixels, filteredPixels } = extractColors(imageData);

      if (extracted.length === 0) {
        if (totalPixels < 4) {
          setError('Image is too small to extract colors.');
        } else if (filteredPixels > totalPixels * 0.9) {
          setError('Image appears to be mostly white or black — try a more colorful image.');
        } else {
          setError('No distinct colors found. The image may be too uniform.');
        }
        setColors([]);
      } else {
        setColors(extracted);
      }
    } catch {
      setError('Could not read colors from this image.');
      setColors([]);
    } finally {
      setLoading(false);
    }
  }, [revokePreview]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  // Global paste handler — works even when drop zone is not focused
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      if (preview) return; // Already showing a result
      const item = Array.from(e.clipboardData?.items || []).find((i) =>
        i.type.startsWith('image/'),
      );
      if (!item) return;
      const file = item.getAsFile();
      if (file) processFile(file);
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [preview, processFile]);

  const toggleSelect = (hex: string) => {
    setSelected((prev) =>
      prev.includes(hex) ? prev.filter((h) => h !== hex) : [hex],
    );
  };

  const handleExplore = () => {
    if (selected.length === 1) {
      navigate(`/color/${selected[0].replace('#', '')}`);
    }
  };

  const reset = () => {
    revokePreview();
    setColors([]);
    setSelected([]);
    setError(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <>
      {preview && (
        <div className="flex justify-end mb-4">
          <button onClick={reset} className="p-1.5 text-ink-muted hover:text-ink-secondary hover:bg-surface-sunken rounded-full transition-colors">
            <X size={15} />
          </button>
        </div>
      )}

      {/* Hidden canvas for pixel processing */}
      <canvas ref={canvasRef} className="hidden" />

      {!preview ? (
        /* Drop zone */
        <div
          className="border-2 border-dashed border-surface-border rounded-xl p-8 text-center cursor-pointer hover:border-brand-400 hover:bg-brand-50 transition-colors"
          onClick={() => inputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && inputRef.current?.click()}
          aria-label="Upload image to extract colors"
        >
          <Upload size={28} className="mx-auto text-ink-faint mb-3" />
          <p className="text-sm text-ink-secondary font-medium">Drop image, click, or paste</p>
          <p className="text-xs text-ink-muted mt-1">PNG · JPG · WebP · GIF</p>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
          />
        </div>
      ) : (
        <div className="space-y-4">
          {/* Image preview */}
          <div className="relative">
            <img
              src={preview}
              alt="Uploaded"
              className="w-full max-h-40 object-cover rounded-xl border border-surface-border"
            />
          </div>

          {loading && (
            <div className="flex items-center justify-center gap-2 py-8">
              <div className="w-5 h-5 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
              <span className="text-sm text-ink-secondary">Extracting colors...</span>
            </div>
          )}

          {error && (
            <p className="text-sm text-red-500 text-center">{error}</p>
          )}

          {colors.length > 0 && (
            <>
              <p className="text-xs text-ink-secondary">
                Select a color to explore
              </p>
              <div className="grid grid-cols-3 gap-2">
                {colors.map(({ hex }) => {
                  const isSelected = selected.includes(hex);
                  return (
                    <button
                      key={hex}
                      onClick={() => toggleSelect(hex)}
                      className={`relative h-14 rounded-lg border-2 transition-all ${
                        isSelected
                          ? 'border-brand-500 scale-105 shadow-md'
                          : 'border-transparent hover:border-surface-border-strong'
                      }`}
                      style={{ background: hex }}
                      title={hex}
                    >
                      {isSelected && (
                        <span className="absolute top-1 right-1 w-4 h-4 bg-brand-600 rounded-full text-white text-xs flex items-center justify-center font-bold">
                          {selected.indexOf(hex) + 1}
                        </span>
                      )}
                      <span
                        className="absolute bottom-1 left-1 text-xs font-mono px-1 rounded"
                        style={{ background: 'rgba(0,0,0,0.4)', color: '#fff' }}
                      >
                        {hex.toUpperCase()}
                      </span>
                    </button>
                  );
                })}
              </div>

              {selected.length > 0 && (
                <button
                  onClick={handleExplore}
                  className="w-full flex items-center justify-center gap-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium py-2.5 rounded-xl transition-colors active:scale-95"
                >
                  Explore This Color
                  <ArrowRight size={15} />
                </button>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};

export default ImageColorExtractor;
